/** Badge shape rendered by sidebar / admin-side-menu. */
export type NavigationItemBadge = {
  text?: string;
  class?: string;
};

export type NavigationItemType = 'dropdown' | 'header' | 'simple' | 'menu' | 'customEntity';

/** Editable navigation item used by the menu items editor (not GraphQL __typename). */
export type EditableNavigationItem = {
  title?: string;
  link?: string;
  key?: string;
  icon?: string;
  type?: NavigationItemType | string;
  /** When type is `menu`, UUID of the Menu whose root items are inlined by getAutoMenu. */
  menuId?: string;
  /** When type is `customEntity`, CustomEntitySchema.identifier for `/customEntity/list/{id}`. */
  customEntityIdentifier?: string;
  badge?: NavigationItemBadge | null;
  allowFavorite?: boolean;
  /** When true, omitted from getAutoMenu / sidebar. */
  hidden?: boolean;
  requiresPermission?: string[];
  requiredRoleIds?: string[];
  children?: EditableNavigationItem[];
};

/** Leaf types allowed under a dropdown (and as root navigable items). */
export const NAVIGABLE_LEAF_TYPES: readonly NavigationItemType[] = ['simple', 'customEntity'];

export function isNavigableLeafType(type: string | undefined): boolean {
  return NAVIGABLE_LEAF_TYPES.includes((type || 'simple') as NavigationItemType);
}

export function buildCustomEntityListLink(identifier: string): string {
  return `/customEntity/list/${identifier}`;
}

export function createDefaultNavigationItem(): EditableNavigationItem {
  return {
    title: '',
    type: 'simple',
    allowFavorite: false,
    children: [],
  };
}

export function parseMenuItemsAsJson(value: unknown): EditableNavigationItem[] {
  if (value == null || value === '') {
    return [];
  }

  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
      // Legacy double-encoding: backend stored JSON.stringify(...) into simple-json
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    return [];
  }

  return parsed.map(item => normalizeNavigationItem(item));
}

export function normalizeNavigationItem(raw: unknown): EditableNavigationItem {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return createDefaultNavigationItem();
  }

  const source = raw as Record<string, unknown>;
  const childrenRaw = source['children'];
  const children = Array.isArray(childrenRaw)
    ? childrenRaw.map(child => normalizeNavigationItem(child))
    : [];

  const badgeRaw = source['badge'];
  let badge: NavigationItemBadge | null | undefined;
  if (badgeRaw && typeof badgeRaw === 'object' && !Array.isArray(badgeRaw)) {
    const badgeObj = badgeRaw as Record<string, unknown>;
    badge = {
      text: typeof badgeObj['text'] === 'string' ? badgeObj['text'] : undefined,
      class: typeof badgeObj['class'] === 'string' ? badgeObj['class'] : undefined,
    };
  } else {
    badge = undefined;
  }

  return {
    title: typeof source['title'] === 'string' ? source['title'] : undefined,
    link: typeof source['link'] === 'string' ? source['link'] : undefined,
    key: typeof source['key'] === 'string' ? source['key'] : undefined,
    icon: typeof source['icon'] === 'string' ? source['icon'] : undefined,
    type: typeof source['type'] === 'string' ? source['type'] : 'simple',
    menuId: typeof source['menuId'] === 'string' ? source['menuId'] : undefined,
    customEntityIdentifier:
      typeof source['customEntityIdentifier'] === 'string'
        ? source['customEntityIdentifier']
        : undefined,
    badge,
    allowFavorite: source['allowFavorite'] === true,
    hidden: source['hidden'] === true,
    requiresPermission: Array.isArray(source['requiresPermission'])
      ? source['requiresPermission'].filter((p): p is string => typeof p === 'string')
      : [],
    requiredRoleIds: Array.isArray(source['requiredRoleIds'])
      ? source['requiredRoleIds'].filter((id): id is string => typeof id === 'string')
      : [],
    children,
  };
}

export function serializeNavigationItems(items: EditableNavigationItem[]): EditableNavigationItem[] {
  return items.map(item => sanitizeNavigationItem(item));
}

function sanitizeNavigationItem(item: EditableNavigationItem): EditableNavigationItem {
  const children = (item.children ?? []).map(child => sanitizeNavigationItem(child));
  const type = item.type || 'simple';
  const badgeText = item.badge?.text?.trim() ?? '';
  const badgeClass = item.badge?.class?.trim() ?? '';
  const badge =
    badgeText || badgeClass
      ? {
        ...(badgeText ? { text: badgeText } : {}),
        ...(badgeClass ? { class: badgeClass } : {}),
      }
      : undefined;

  const result: EditableNavigationItem = {
    type,
  };

  if (type !== 'menu') {
    result.title = item.title ?? '';
  }

  if (type === 'simple' && item.link) {
    result.link = item.link;
  }
  if (type === 'customEntity') {
    const identifier = item.customEntityIdentifier?.trim();
    if (identifier) {
      result.customEntityIdentifier = identifier;
      result.link = buildCustomEntityListLink(identifier);
    }
  }
  if (item.key) {
    result.key = item.key;
  }
  if ((type === 'dropdown' || isNavigableLeafType(type)) && item.icon) {
    result.icon = item.icon;
  }
  if (isNavigableLeafType(type) && item.allowFavorite) {
    result.allowFavorite = true;
  }
  if (item.hidden) {
    result.hidden = true;
  }
  if (type === 'menu' && item.menuId) {
    result.menuId = item.menuId;
  }
  if (item.requiresPermission?.length) {
    result.requiresPermission = [...item.requiresPermission];
  }
  if (item.requiredRoleIds?.length) {
    result.requiredRoleIds = [...item.requiredRoleIds];
  }
  // Menu refs are spliced away at runtime; headers are labels only — no badge.
  if (badge && type !== 'menu' && type !== 'header') {
    result.badge = badge;
  }
  // Only dropdowns may have children; nested items must be navigable leaves.
  if (type === 'dropdown' && children.length) {
    result.children = children
      .filter(child => isNavigableLeafType(child.type || 'simple'))
      .map(child => {
        const childType = child.type === 'customEntity' ? 'customEntity' : 'simple';
        return {
          ...child,
          type: childType,
          children: undefined,
          icon: undefined,
          menuId: undefined,
        };
      });
  }

  return result;
}

/** True when tree obeys: headers/dropdowns/menus only at root; dropdown children are leaf types only. */
export function isMenuTreeStructureValid(items: EditableNavigationItem[]): boolean {
  for (const item of items) {
    const type = item.type || 'simple';
    const children = item.children ?? [];
    if (type === 'menu') {
      if (children.length || item.link) {
        return false;
      }
      continue;
    }
    if (type === 'dropdown') {
      if (item.link) {
        return false;
      }
      for (const child of children) {
        const childType = child.type || 'simple';
        if (!isNavigableLeafType(childType)) {
          return false;
        }
        if (child.children?.length) {
          return false;
        }
      }
    } else if (children.length) {
      return false;
    }
  }
  return true;
}
